export class ApiError extends Error {}
export class ApiServerError extends ApiError {
    code: number;

    constructor(message: string, code: number) {
        super(message);
        this.code = code;
    }
}


export async function performApiRequest(url: string, method: string, body?: any, headers?: any) {
    let actualHeaders = {
        ...(headers || {}),
        "Content-Type": "application/json"
    }
    let response, responseData;
    try {
        response = await fetch(url, {
            method: method,
            body: body !== undefined ? JSON.stringify(body) : undefined,
            headers: actualHeaders
        });
    } catch (e) {
        throw new ApiError("Connection error");
    }
    try {
        responseData = await response.json();
    } catch (e) {
        throw new ApiError("Error reading json response");
    }
    if (response.status != 200) {
        throw new ApiServerError(responseData?.message || "Server error", response.status);
    }
    return responseData;
}


export interface WsHandle {
    send(data: string): void;
    close(): void;
    /** Resolves when the connection closes (for any reason). */
    consume(): Promise<void>;
    /** Immediately resolve consume() without waiting for the socket to close.
     *  Use when the caller wants to abandon a zombie connection and reconnect. */
    abort(): void;
}

export function consumeWS(
    url: string,
    onMessage: (data: any) => void,
    callbacks?: { onOpen?: () => void },
): WsHandle {
    const ws = new WebSocket(url);
    let resolveConsume!: () => void;
    const consumed = new Promise<void>(resolve => { resolveConsume = resolve; });

    ws.onopen = () => callbacks?.onOpen?.();

    ws.onmessage = (event: MessageEvent) => {
        let data: any;
        try { data = JSON.parse(event.data as string); } catch { return; }
        onMessage(data);
    };

    ws.onclose = () => resolveConsume();

    return {
        send: (data: string) => { if (ws.readyState === WebSocket.OPEN) ws.send(data); },
        close: () => ws.close(),
        consume: () => consumed,
        abort: () => { resolveConsume(); ws.close(); },
    };
}

export function consumeSSE(url: string, onMessage: any, abortSignal: AbortSignal): Promise<void> {
    return new Promise((resolve) => {
        let eventSource = new EventSource(url);
        eventSource.onmessage = (event) => {
            if (event.data == "") {
                eventSource.close();
            } else {
                let data = JSON.parse(event.data);
                onMessage(data);
            }
        }
        eventSource.onerror = (event: any) => {
            if (event.eventPhase == EventSource.CLOSED) {
                eventSource.close();
                resolve();
            }
        }
        abortSignal.addEventListener('abort', () => {
            eventSource.close();
            resolve();
        }, {once: true});
    });
}
